import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  Trophy, 
  Users, 
  Menu, 
  LogOut,
  ChevronRight,
  BookOpen,
  Shield,
  Key,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AECAR_LOGO = "https://customer-assets.emergentagent.com/job_1ec63aee-8cac-40d0-af9b-2322b1e1b60a/artifacts/17qzkh1t_LOGO%20AECAR%20VIDRIO.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/campeonatos', label: 'Campeonatos', icon: Trophy },
  { path: '/pilotos', label: 'Pilotos', icon: Users, adminOnly: true },  // Solo admin
  { path: '/manual', label: 'Manual de Uso', icon: BookOpen },
];

const adminNavItem = { path: '/admin', label: 'Administración', icon: Shield };

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [globalSettings, setGlobalSettings] = useState({});
  
  // Change password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${API}/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: token 
            ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            : { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    
    const fetchGlobalSettings = async () => {
      try {
        const response = await fetch(`${API}/admin/global-settings`);
        if (response.ok) {
          const data = await response.json();
          setGlobalSettings(data);
          
          // Apply background from global settings
          const bgUrl = data.dashboard_background_url || data.background_url;
          if (bgUrl) {
            document.documentElement.style.setProperty('--app-background-image', `url(${bgUrl})`);
          } else {
            document.documentElement.style.removeProperty('--app-background-image');
          }
        }
      } catch (error) {
        console.error('Error fetching global settings:', error);
      }
    };
    
    fetchUser();
    fetchGlobalSettings();
    
    return () => {
      document.documentElement.style.removeProperty('--app-background-image');
    };
  }, []);

  // Get logo from global settings or default
  const logoUrl = globalSettings.logo_url || AECAR_LOGO;

  const handleLogout = async () => {
    // Clear token first to prevent race condition
    localStorage.removeItem('token');
    
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error logging out:', error);
    }
    
    toast.success('Sesión cerrada');
    // Use window.location for a full page reload to clear all state
    window.location.href = '/';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al cambiar contraseña');
      }
      
      toast.success('Contraseña actualizada correctamente');
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(String(error?.message || 'Error'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
          isActive 
            ? 'bg-primary/10 text-primary border-l-2 border-primary' 
            : 'text-muted-foreground hover:text-white hover:bg-white/5'
        }`}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <item.icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col bg-black border-r border-white/10">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
          <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain" />
          <span className="font-heading font-bold text-lg">AECAR</span>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {navItems
            .filter(item => !item.adminOnly || user?.role === 'admin')
            .map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          {user?.role === 'admin' && (
            <NavLink item={adminNavItem} />
          )}
        </nav>
        <div className="p-4 border-t border-white/10">
          {user && (
            <div className="flex items-center gap-3 px-2">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user.picture} />
                <AvatarFallback className="bg-primary/20 text-primary">
                  {user.name?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-strong border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src={logoUrl} alt="Logo" className="w-8 h-8 object-contain" />
            <span className="font-heading font-bold text-base">AECAR</span>
          </div>
          <div className="flex items-center gap-2">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-black border-white/10 p-0">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
                  <img src={AECAR_LOGO} alt="AECAR" className="w-10 h-10 object-contain" />
                  <span className="font-heading font-bold text-lg">AECAR</span>
                </div>
                <nav className="px-4 py-6 space-y-1">
                  {navItems
                    .filter(item => !item.adminOnly || user?.role === 'admin')
                    .map((item) => (
                      <NavLink 
                        key={item.path} 
                        item={item} 
                        onClick={() => setMobileMenuOpen(false)}
                      />
                    ))}
                  {user?.role === 'admin' && (
                    <NavLink 
                      item={adminNavItem} 
                      onClick={() => setMobileMenuOpen(false)}
                    />
                  )}
                </nav>
                {user && (
                  <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
                    <div className="flex items-center gap-3 px-2 mb-4">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback className="bg-primary/20 text-primary">
                          {user.name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full border-white/10" 
                      onClick={handleLogout}
                      data-testid="mobile-logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Cerrar Sesión
                    </Button>
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Desktop Top Bar */}
      <header className="hidden lg:block fixed top-0 left-64 right-0 z-40 glass-strong border-b border-white/10">
        <div className="flex items-center justify-between px-6 py-3">
          <div>
            <h1 className="font-heading font-bold text-lg uppercase">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary/20 text-primary">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-card border-white/10">
              <DropdownMenuItem onClick={() => setShowPasswordModal(true)} data-testid="change-password-btn">
                <Key className="w-4 h-4 mr-2" />
                Cambiar Contraseña
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-14 min-h-screen">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="bg-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Cambiar Contraseña</DialogTitle>
            <DialogDescription>
              Introduce tu contraseña actual y la nueva contraseña
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Contraseña Actual</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="bg-black/20 border-white/10"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva Contraseña</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="bg-black/20 border-white/10"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar Nueva Contraseña</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="bg-black/20 border-white/10"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordModal(false)}
                className="border-white/10"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={passwordLoading} data-testid="save-password-btn">
                {passwordLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
